import React from "react";
import { Layout, Space, Typography, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { FooterOptions } from "../../../types/dashboard";

const { Footer } = Layout;
const { Text } = Typography;

type Props = {
  footerOptions: FooterOptions;
  footerLeft: string;
  footerRight: string;
  editingLeft: boolean;
  editingRight: boolean;
  setFooterLeft: (v: string) => void;
  setFooterRight: (v: string) => void;
  setEditingLeft: (v: boolean) => void;
  setEditingRight: (v: boolean) => void;
  centered?: boolean;
};

const FooterSection: React.FC<Props> = (p) => {
  const { t } = useTranslation("dashboard");
  const { centered = false } = p;
  const color = p.footerOptions.textColor;
  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "https://developmentapi.kapinsights.com";

  const getAbsoluteImageUrl = (path?: string | null) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API_BASE_URL}${path}`;
  };

  const footerImageSrc = getAbsoluteImageUrl(p.footerOptions.imageUrl);
  return (
    <Footer
      style={{
        background: p.footerOptions.backgroundColor,
        padding: `${p.footerOptions.paddingY}px ${
          p.footerOptions.paddingRight ?? p.footerOptions.paddingX
        }px ${p.footerOptions.paddingY}px ${
          p.footerOptions.paddingLeft ?? p.footerOptions.paddingX
        }px`,
        borderTop: `${p.footerOptions.borderWidth}px solid ${p.footerOptions.borderColor}`,
        borderRadius: `0 0 ${p.footerOptions.radius}px ${p.footerOptions.radius}px`,
      }}
    >
      {/* ───────── Centered mode ───────── */}
      {centered ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {footerImageSrc && p.footerOptions.imageAlign === "left" && (
            <img
              src={footerImageSrc}
              alt="footer"
              style={{ height: 28, marginRight: 12, display: "block" }}
            />
          )}
          {/* Edito secilën pjesë në vend (inline) */}
          {p.editingLeft ? (
            <Input
              value={p.footerLeft}
              onChange={(e) => p.setFooterLeft(e.target.value)}
              onPressEnter={() => p.setEditingLeft(false)}
              onBlur={() => p.setEditingLeft(false)}
              style={{ maxWidth: 520, marginRight: 8 }}
            />
          ) : (
            <Text
              style={{
                color,
                fontSize: p.footerOptions.fontSize,
                fontFamily: p.footerOptions.fontFamily,
                lineHeight:
                  typeof p.footerOptions.lineHeight === "number"
                    ? String(p.footerOptions.lineHeight)
                    : p.footerOptions.lineHeight,
              }}
            >
              {p.footerLeft}
              <EditOutlined
                onClick={() => p.setEditingLeft(true)}
                style={{ marginLeft: 8, fontSize: 14, cursor: "pointer" }}
              />
            </Text>
          )}

          <Text style={{ color, opacity: 0.6, margin: "0 10px" }}>•</Text>

          {p.editingRight ? (
            <Input
              value={p.footerRight}
              onChange={(e) => p.setFooterRight(e.target.value)}
              onPressEnter={() => p.setEditingRight(false)}
              onBlur={() => p.setEditingRight(false)}
              style={{ maxWidth: 320, marginLeft: 8 }}
            />
          ) : (
            <Text style={{ color }}>
              {p.footerRight}
              <EditOutlined
                onClick={() => p.setEditingRight(true)}
                style={{ marginLeft: 8, fontSize: 14, cursor: "pointer" }}
              />
            </Text>
          )}

          {footerImageSrc && p.footerOptions.imageAlign === "right" && (
            <img
              src={footerImageSrc}
              alt="footer"
              style={{ height: 28, marginLeft: 12, display: "block" }}
            />
          )}
        </div>
      ) : (
        /* ───────── Mode i vjetër (majtas/djathtas) ───────── */
        <Space
          style={{
            width: "100%",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          {p.editingLeft ? (
            <Input
              value={p.footerLeft}
              onChange={(e) => p.setFooterLeft(e.target.value)}
              onPressEnter={() => p.setEditingLeft(false)}
              onBlur={() => p.setEditingLeft(false)}
              style={{ minWidth: 320 }}
            />
          ) : (
            <Text style={{ color }}>
              {p.footerLeft}{" "}
              <EditOutlined
                onClick={() => p.setEditingLeft(true)}
                style={{ marginLeft: 8, fontSize: 14, cursor: "pointer" }}
              />
            </Text>
          )}

          {p.editingRight ? (
            <Input
              value={p.footerRight}
              onChange={(e) => p.setFooterRight(e.target.value)}
              onPressEnter={() => p.setEditingRight(false)}
              onBlur={() => p.setEditingRight(false)}
              style={{ minWidth: 220 }}
            />
          ) : (
            <Text style={{ color }}>
              {p.footerRight}{" "}
              <EditOutlined
                onClick={() => p.setEditingRight(true)}
                style={{ marginLeft: 8, fontSize: 14, cursor: "pointer" }}
              />
            </Text>
          )}
        </Space>
      )}
    </Footer>
  );
};

export default React.memo(FooterSection);
